import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaHistory } from "react-icons/fa";
import Spinner from "../../components/Spinner";
import http from "../../api/apiClient";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { formatDutchDate } from "../../utils/dutchDate";

/** Dates come as [year, month, day] (Jackson) or "yyyy-MM-dd". */
type DateValue = string | number[] | null;
type ImportPeriodView = { importedFrom: DateValue; importedThrough: DateValue };
type ImportResult = { imported: number; linkedByIban: number; linkedByRelation: number; waiting: number };

const ENDPOINT = "/finance/settings/eboekhouden/import-period";
const MAX_YEARS_BACK = 5;

const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * What has been fetched from e-Boekhouden (a next fetch only reads what is new), and fetching once from an earlier
 * date. Fetching needs the manage right on the contributions.
 */
export default function ImportPeriod({ canImport }: { canImport: boolean }): ReactElement {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<ImportPeriodView | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(() => http.get(ENDPOINT)
    .then(({ data }) => setPeriod(data ?? null))
    .catch((caught) => setMessage({ ok: false, text: mapHttpErrorToMessage(caught, t).message || "" })), [t]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const errorText = (caught: any) => {
    switch (caught?.response?.data?.message) {
      case "IMPORT_FROM_IN_FUTURE": return t("finance.settings.import_period.errors.future", { defaultValue: "The date may not lie in the future." });
      case "IMPORT_TO_BEFORE_FROM": return t("finance.settings.import_period.errors.to_before_from", { defaultValue: "The end date lies before the start date." });
      case "IMPORT_FROM_TOO_EARLY": return t("finance.settings.import_period.errors.too_early", { years: MAX_YEARS_BACK, defaultValue: "At most {{years}} years back." });
      case "EBOEKHOUDEN_NOT_CONFIGURED": return t("finance.payments.import_errors.not_configured", { defaultValue: "The connection with e-Boekhouden is not set up yet. See Finance › Settings." });
      case "EBOEKHOUDEN_DISABLED": return t("finance.payments.import_errors.disabled", { defaultValue: "The connection with e-Boekhouden is switched off. See Finance › Settings." });
      case "EBOEKHOUDEN_UNREACHABLE": return t("finance.payments.import_errors.unreachable", { defaultValue: "e-Boekhouden could not be reached. Try again later." });
      case "EBOEKHOUDEN_REJECTED": return t("finance.payments.import_errors.rejected", { defaultValue: "e-Boekhouden refused the request. Check the token in the settings." });
      default: return mapHttpErrorToMessage(caught, t).message || t("finance.payments.import_errors.error", { defaultValue: "Reading payments failed." });
    }
  };

  const fetchOnce = async () => {
    if (!from) return;
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await http.post("/finance/payments/import", null, { params: to ? { from, to } : { from } });
      const result = data as ImportResult;
      setMessage({
        ok: true,
        text: t("finance.payments.import_result", {
          imported: result?.imported ?? 0,
          iban: result?.linkedByIban ?? 0,
          relation: result?.linkedByRelation ?? 0,
          waiting: result?.waiting ?? 0,
          defaultValue: "{{imported}} new payment(s): {{iban}} linked by IBAN, {{relation}} by an earlier link, {{waiting}} to link.",
        }),
      });
      setFrom("");
      setTo("");
      await load();
    } catch (caught) {
      setMessage({ ok: false, text: errorText(caught) });
    } finally {
      setBusy(false);
    }
  };

  const today = new Date();
  const earliest = new Date(today.getFullYear() - MAX_YEARS_BACK, today.getMonth(), today.getDate());
  const importedFrom = period?.importedFrom ?? null;
  const importedThrough = period?.importedThrough ?? null;

  let status: string;
  if (!importedFrom) {
    status = t("finance.settings.import_period.none", { defaultValue: "No fetched period recorded yet. The next fetch reads once from 1 June before the school year; payments already in Lumen are not fetched twice." });
  } else if (!importedThrough) {
    status = t("finance.settings.import_period.refetch", {
      date: formatDutchDate(importedFrom),
      defaultValue: "The ledgers have changed: the next fetch reads everything from {{date}} once more.",
    });
  } else {
    status = t("finance.settings.import_period.fetched", {
      from: formatDutchDate(importedFrom),
      through: formatDutchDate(importedThrough),
      defaultValue: "Fetched from {{from}} up to {{through}}.",
    });
  }

  return (
    <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
      <h2 className="text-base font-bold text-slate-950 dark:text-white">
        {t("finance.settings.import_period.title", { defaultValue: "Fetched period" })}
      </h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {t("finance.settings.import_period.help", {
          defaultValue: "A fetch only reads what is new since the last successful fetch (with a margin of a month for bookings entered later). After changing the ledgers everything is read once more.",
        })}
      </p>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <p className="mt-3 text-sm font-semibold text-slate-800 dark:text-slate-100">{status}</p>
          {canImport && (
            <div className="mt-4">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {t("finance.settings.import_period.once_help", {
                  years: MAX_YEARS_BACK,
                  defaultValue: "Fetch older payments once, for a period from–to (empty end date = today; at most {{years}} years back). Payments already fetched are not fetched twice.",
                })}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={from}
                  min={isoDate(earliest)}
                  max={isoDate(today)}
                  onChange={(event) => setFrom(event.target.value)}
                  aria-label={t("finance.settings.import_period.date", { defaultValue: "Fetch from date" })}
                  className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-white/10 dark:bg-slate-900 dark:text-white"
                />
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {t("finance.settings.import_period.up_to", { defaultValue: "up to" })}
                </span>
                <input
                  type="date"
                  value={to}
                  min={from || isoDate(earliest)}
                  max={isoDate(today)}
                  onChange={(event) => setTo(event.target.value)}
                  aria-label={t("finance.settings.import_period.to_date", { defaultValue: "Fetch up to date (empty = today)" })}
                  placeholder={t("finance.settings.import_period.today", { defaultValue: "today" })}
                  className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-white/10 dark:bg-slate-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={fetchOnce}
                  disabled={busy || !from || Boolean(to && to < from)}
                  className="inline-flex h-10 items-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <FaHistory aria-hidden="true" />
                  {busy
                    ? t("finance.settings.import_period.fetching", { defaultValue: "Fetching…" })
                    : t("finance.settings.import_period.fetch_once", { defaultValue: "Fetch once" })}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {message && (
        <p role={message.ok ? "status" : "alert"} className={`mt-3 text-sm font-semibold ${message.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-600"}`}>
          {message.text}
        </p>
      )}
    </section>
  );
}
